import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useEffect, useState, type ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { api, describeError } from '../../api/client'
import type { DailyReport } from '../../api/execution'
import { useMySites } from '../../api/hooks'
import { LoadingBlock } from '../../components/Feedback'
import { PageHeader } from '../../components/PageHeader'
import { ReportStatusChip } from '../../components/ReportStatusChip'

/**
 * Site and date picker that resolves the day's report before showing an entry form.
 *
 * <p>A site engineer thinks "I need to enter today's labour", not "I need to open a report
 * and find a tab". This shell finds the report for the chosen site and date, offers to
 * create it if there is none, and only then renders the form — so the entry screens are
 * reachable directly from the menu.</p>
 */
export function DayEntryShell({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle: string
  children: (report: DailyReport, editable: boolean) => ReactNode
}) {
  const sites = useMySites()
  const [siteId, setSiteId] = useState('')
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [report, setReport] = useState<DailyReport | null>(null)
  const [state, setState] = useState<'idle' | 'loading' | 'none' | 'ready' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  // Default to the engineer's own site so the common case needs no selection at all.
  useEffect(() => {
    if (!siteId && sites.data?.length) {
      setSiteId(sites.data[0].id)
    }
  }, [sites.data, siteId])

  useEffect(() => {
    if (!siteId || !date) return
    let cancelled = false
    setState('loading')
    setError(null)

    api
      .get<DailyReport>('/daily-reports/by-date', { params: { siteId, date, shift: 'DAY' } })
      .then((response) => {
        if (cancelled) return
        // 204 means no report exists for that day yet.
        if (response.status === 204 || !response.data) {
          setReport(null)
          setState('none')
        } else {
          setReport(response.data)
          setState('ready')
        }
      })
      .catch((err) => {
        if (cancelled) return
        setError(describeError(err))
        setState('error')
      })

    return () => {
      cancelled = true
    }
  }, [siteId, date])

  async function createReport() {
    setCreating(true)
    setError(null)
    try {
      const { data } = await api.post<DailyReport>('/daily-reports', {
        siteId,
        reportDate: date,
        shift: 'DAY',
      })
      setReport(data)
      setState('ready')
    } catch (err) {
      setError(describeError(err))
    } finally {
      setCreating(false)
    }
  }

  const site = sites.data?.find((s) => s.id === siteId)

  return (
    <>
      <PageHeader title={title} subtitle={subtitle} />

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField select label="Site" value={siteId} onChange={(e) => setSiteId(e.target.value)}
                   sx={{ minWidth: 260 }}>
          {(sites.data ?? []).map((s) => (
            <MenuItem key={s.id} value={s.id}>{s.code} - {s.name}</MenuItem>
          ))}
        </TextField>
        <TextField label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   slotProps={{ inputLabel: { shrink: true } }} />
        <Box sx={{ flex: 1 }} />
        {report && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {report.reportNumber}
            </Typography>
            <ReportStatusChip status={report.status} />
            <Link component={RouterLink} to={`/daily-reports/${report.id}`} variant="body2">
              open full report
            </Link>
          </Box>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {state === 'loading' && <LoadingBlock label="Finding the day's report..." />}

      {state === 'none' && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No report yet for {site?.code ?? 'this site'} on {date}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Everything recorded for a day hangs off its daily report. Create it and you can
            start entering straight away.
          </Typography>
          <Button variant="contained" onClick={createReport} disabled={creating}>
            {creating ? 'Creating...' : `Create the report for ${date}`}
          </Button>
        </Paper>
      )}

      {state === 'ready' && report && !report.editable && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>This day is locked</AlertTitle>
          The report is <strong>{report.status.toLowerCase().replace('_', ' ')}</strong>, so nothing
          can be added or changed. A reviewer has to reopen it first — that is what keeps an
          approved day from quietly changing afterwards. Pick another date, or{' '}
          <Link component={RouterLink} to={`/daily-reports/${report.id}`}>open the report</Link> to
          see who approved it.
        </Alert>
      )}

      {state === 'ready' && report && children(report, report.editable)}
    </>
  )
}
